import { useCallback, useEffect, useRef, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { css } from '@emotion/css';
import {
  GetTitleSearchOwnerQuery,
  PropertyTitleOwnerOutput,
  PropertyTitleSearchAddressInput,
  useGetTitleSearchOwnerQuery,
  useStartPropertyTitleSearchMutation,
} from '@graphql/index';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Divider } from '@spark-web/divider';
import { Heading } from '@spark-web/heading';
import { HomeIcon } from '@spark-web/icon';
import { Spinner } from '@spark-web/spinner';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import {
  LAND_TITLE_POLL_INTERVAL_MS,
  LAND_TITLE_POLL_TIMEOUT_MS,
  LAND_TITLE_SEARCH_WAIT_MESSAGES,
  ONE_MINUTE_MS,
} from '../../utils/constants';
import EligibilityPageHeading from './EligibilityPageHeading';
import EligibilityStepHeader from './EligibilityStepHeader';

const TERMINAL_STATUSES = new Set(['PASSED', 'EXPIRED', 'CANCELLED']);

type PropertyTitleCheckProps = {
  address: string;
  rawAddress: PropertyTitleSearchAddressInput;
  trackingId: string;
  productName: string;
  onContinue: (owners: PropertyTitleOwnerOutput[]) => void;
  onContinueToApplication: () => void;
  onBack: () => void;
};

function PropertyTitleCheck({
  address,
  rawAddress,
  trackingId,
  productName,
  onContinue,
  onContinueToApplication,
  onBack,
}: PropertyTitleCheckProps) {
  const [searchStarted, setSearchStarted] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  // Wall-clock start of the polling window. Held in a ref so the deadline can be
  // recomputed on demand (see `hasExceededWait`) rather than trusted from a
  // timer that a background tab may have throttled or frozen.
  const startedAtRef = useRef<number | null>(null);

  const { mutate: startSearch } = useStartPropertyTitleSearchMutation({
    onSuccess: () => setSearchStarted(true),
    onError: () => setSearchFailed(true),
  });

  useEffect(() => {
    startSearch({ input: { trackingId, address: rawAddress } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start the clock when polling actually begins, not on mount. startPropertyTitleSearch
  // performs two synchronous Equifax calls (search-land-titles, then order-document)
  // before it resolves, and polling is gated on `searchStarted`. Counting that
  // handshake against the wait budget silently shortened it exactly when Equifax
  // was slow — the case the budget exists for.
  useEffect(() => {
    if (!searchStarted || startedAtRef.current !== null) return;
    startedAtRef.current = Date.now();
    setElapsedMs(0);
  }, [searchStarted]);

  const hasExceededWait = useCallback(
    () => startedAtRef.current !== null && Date.now() - startedAtRef.current >= LAND_TITLE_POLL_TIMEOUT_MS,
    [],
  );

  const { data: titleResult, refetch } = useGetTitleSearchOwnerQuery(
    { input: { applicationId: trackingId } },
    {
      enabled: searchStarted,
      select: (data: GetTitleSearchOwnerQuery) => data.getTitleSearchOwner,
      refetchInterval: data => {
        if (hasExceededWait()) return false;
        if (!data || !TERMINAL_STATUSES.has(data.searchStatus)) return LAND_TITLE_POLL_INTERVAL_MS;
        return false;
      },
      // Without this, react-query suspends refetchInterval whenever the tab is
      // backgrounded. Customers routinely switch tabs while waiting, which left
      // the poll clock stopped while the wait clock kept running — they returned
      // to a timed-out screen for a search that had in fact succeeded.
      refetchIntervalInBackground: true,
    },
  );

  // Timers in a hidden tab are throttled and may be frozen outright, so the
  // elapsed value can be stale on return. Recompute it and poll once immediately
  // whenever the tab becomes visible, rather than waiting for the next interval.
  useEffect(() => {
    if (!searchStarted) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || startedAtRef.current === null) return;
      setElapsedMs(Date.now() - startedAtRef.current);
      if (!hasExceededWait()) void refetch();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [searchStarted, hasExceededWait, refetch]);

  const timedOut = elapsedMs >= LAND_TITLE_POLL_TIMEOUT_MS;

  // Drives the on-screen wait copy only — never the timeout decision. Stops once
  // the wait is over: past that point the fallback UI is showing and there is no
  // message left to advance, so ticking on would re-render every second for as
  // long as the customer sits on that screen.
  useEffect(() => {
    if (!searchStarted || timedOut) return;
    const tick = setInterval(() => {
      if (startedAtRef.current !== null) setElapsedMs(Date.now() - startedAtRef.current);
    }, 1000);
    return () => clearInterval(tick);
  }, [searchStarted, timedOut]);

  // Math.max guards a backwards system-clock step (an NTP correction mid-wait),
  // which would otherwise index the array at -1 and throw on `.title`.
  const waitMessage =
    LAND_TITLE_SEARCH_WAIT_MESSAGES[
      Math.min(Math.max(0, Math.floor(elapsedMs / ONE_MINUTE_MS)), LAND_TITLE_SEARCH_WAIT_MESSAGES.length - 1)
    ];

  const isTerminal = titleResult !== undefined && TERMINAL_STATUSES.has(titleResult.searchStatus);
  const hasOwners = (titleResult?.owners?.length ?? 0) > 0;
  const isPassed = isTerminal && titleResult?.searchStatus === 'PASSED' && hasOwners;
  const showFallback = timedOut || searchFailed || (isTerminal && !hasOwners);

  useEffect(() => {
    if (isPassed) {
      onContinue(titleResult.owners as PropertyTitleOwnerOutput[]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPassed]);

  useEffect(() => {
    if (!showFallback) return;
    const reason = searchFailed ? 'search_failed' : timedOut ? 'timed_out' : 'no_owners_found';
    datadogRum.addAction('LandOwner Title Check Unavailable', { trackingId, reason });
  }, [showFallback]);

  return (
    <Stack padding={{ tablet: 'xlarge' }} gap="xxlarge">
      <EligibilityStepHeader currentStep={3} totalSteps={5} />

      <EligibilityPageHeading productName={productName}>
        <Heading level="1">
          {showFallback ? "Who's on the property title?" : 'Checking certificate of title...'}
        </Heading>
      </EligibilityPageHeading>

      <Stack gap="xlarge">
        {showFallback && (
          <Text size="standard">
            Before we can verify your household income, we need to confirm who&apos;s the owner of the property on the
            installation address. We do this by checking the Certificate of title through the land registry.
          </Text>
        )}

        <Box borderRadius="medium" background="surfaceMuted" borderWidth="standard" border="standard">
          <Box display="flex" alignItems="center" gap="medium" padding="large">
            <Box flexShrink={0}>
              <HomeIcon size="small" tone="primary" />
            </Box>
            <Stack gap="medium">
              {showFallback && (
                <Text weight="semibold" size="standard">
                  Installation address
                </Text>
              )}
              <Text size="standard">{address}</Text>
            </Stack>
          </Box>

          {!showFallback && (
            <>
              <Divider />
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="medium"
                paddingX="xlarge"
                paddingY="xxxlarge"
              >
                <Box className={css({ transform: 'scale(1.3333)' })}>
                  <Spinner tone="positive" size="xsmall" />
                </Box>
                <Stack gap="large" align="center">
                  <Text size="large" weight="semibold" align="center">
                    {waitMessage.title}
                  </Text>
                  <Text size="small" tone="muted" align="center">
                    {waitMessage.subtitle}
                  </Text>
                </Stack>
              </Box>
            </>
          )}
        </Box>

        {showFallback && (
          <>
            <Alert tone="caution" heading="We're unable to retrieve your land title owners.">
              Our team will be in touch to verify property ownership before your application can be approved.
            </Alert>

            <Stack gap="large">
              <Button tone="primary" onClick={onContinueToApplication}>
                Continue to application
              </Button>
              <Button prominence="none" tone="neutral" onClick={onBack}>
                Back
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Stack>
  );
}

export default PropertyTitleCheck;
